import { AnimatedAiIndicator } from '@/components/chat/animated-ai-indicator';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const MAX_TOOL_PILLS = 3;

interface ComposerCapabilitiesStripProps {
  summary: string;
  isStreaming?: boolean;
  isLoading?: boolean;
  toolNames?: string[];
  centered?: boolean;
  className?: string;
}

export function ComposerCapabilitiesStrip({
  summary,
  isStreaming = false,
  isLoading = false,
  toolNames = [],
  centered = false,
  className,
}: ComposerCapabilitiesStripProps) {
  const trimmedSummary = summary.trim();
  const visibleTools = toolNames.filter(Boolean).slice(0, MAX_TOOL_PILLS);
  const overflowCount = Math.max(0, toolNames.filter(Boolean).length - MAX_TOOL_PILLS);

  if (!trimmedSummary && !isStreaming && visibleTools.length === 0) {
    return null;
  }

  const displayText =
    trimmedSummary || (isStreaming ? 'Loading capabilities…' : '');

  return (
    <div
      className={cn(
        'flex min-w-0 items-start gap-2 border-b border-border/60',
        centered ? 'px-4 py-2.5' : 'px-3 py-2',
        className
      )}
    >
      <AnimatedAiIndicator isLoading={isLoading} centered={centered} />
      <div className="min-w-0 flex-1 space-y-1.5">
        {displayText ? (
          <p
            className="text-xs leading-snug text-muted-foreground line-clamp-2"
            title={trimmedSummary || undefined}
          >
            {displayText}
          </p>
        ) : null}
        {visibleTools.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1">
            {visibleTools.map((name) => (
              <Badge key={name} variant="secondarySoft" className="text-[10px] font-normal">
                {name}
              </Badge>
            ))}
            {overflowCount > 0 ? (
              <Badge variant="secondarySoft" className="text-[10px] font-normal">
                +{overflowCount}
              </Badge>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
